import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { loadFeedDirectory, type FeedDirectory } from "./feed-directory.js";
import { feedHealthStore, type FeedHealthState } from "./health-store.js";
import { fetchHealthPolicy } from "./health.js";
import { fetchFeed, fetchFeeds, fetchHistory, fetchNextUp } from "./feeds.js";
import { fetchTrackPage } from "./library.js";
import { fetchOutputs } from "./outputs.js";
import {
  fetchPlaylist,
  fetchPlaylistPage,
  fetchPlaylists,
  fetchSchedules,
  fetchSlots,
} from "./programming.js";
import type { Page } from "./collection.js";
import { nowPlayingStore, type FeedNowPlaying } from "./now-playing-store.js";
import { readSession, subscribeSession } from "./session-store.js";
import type { OperatorSession } from "./session-store.js";
import type { ResourceState, ResourceStore } from "./resource-store.js";
import type {
  Airing,
  Decision,
  Feed,
  HealthPolicy,
  Output,
  Playlist,
  PlaylistDetail,
  Schedule,
  ScheduleSlot,
  Track,
} from "./types.js";

const NOTHING_PLAYING: FeedNowPlaying = {
  data: null,
  error: null,
  loading: false,
  updatedAt: null,
  failures: 0,
};

/**
 * Live state for one feed, from the one poller that feed has. A null feed — the
 * player before anything is playing — subscribes to nothing and polls nothing.
 */
export function useNowPlaying(feedId: string | null): FeedNowPlaying {
  const subscribe = useCallback(
    (listener: () => void) =>
      feedId === null
        ? () => undefined
        : nowPlayingStore.subscribe(feedId, listener),
    [feedId],
  );
  const getSnapshot = useCallback(
    () =>
      feedId === null ? NOTHING_PLAYING : nowPlayingStore.getState(feedId),
    [feedId],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

const NOTHING_ESTABLISHED: FeedHealthState = {
  data: null,
  error: null,
  loading: false,
  updatedAt: null,
  failures: 0,
};

/**
 * What the product has established about one feed, on that feed's own clock. Only
 * asked for when the operator is signed in: this is the authenticated surface, and an
 * anonymous visitor reading it would collect nothing but 401s.
 */
export function useFeedHealthState(
  feedId: string | null,
  enabled = true,
): FeedHealthState {
  const key = enabled ? feedId : null;
  const subscribe = useCallback(
    (listener: () => void) =>
      key === null ? () => undefined : feedHealthStore.subscribe(key, listener),
    [key],
  );
  const getSnapshot = useCallback(
    () => (key === null ? NOTHING_ESTABLISHED : feedHealthStore.getState(key)),
    [key],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * The same live value for a set of feeds at once, which is what a console of N feeds
 * needs and what a component per feed cannot give it — a screen cannot call a hook
 * once per row without fixing how many rows there are.
 *
 * The snapshot is cached against the exact state objects it was built from, so the
 * record is referentially stable until one of the feeds in it actually changes. That
 * is what `useSyncExternalStore` requires and what keeps a console of ten feeds from
 * re-rendering every time any one of them is polled.
 */
function useStoreMap<T>(
  store: ResourceStore<T>,
  keys: readonly string[],
): Record<string, ResourceState<T>> {
  const signature = keys.join(" ");
  const cache = useRef<{
    signature: string;
    states: ResourceState<T>[];
    value: Record<string, ResourceState<T>>;
  } | null>(null);

  const subscribe = useCallback(
    (listener: () => void) => {
      const unsubscribes = splitKeys(signature).map((key) =>
        store.subscribe(key, listener),
      );
      return () => {
        for (const unsubscribe of unsubscribes) unsubscribe();
      };
    },
    [signature, store],
  );

  const getSnapshot = useCallback(() => {
    const list = splitKeys(signature);
    const states = list.map((key) => store.getState(key));
    const previous = cache.current;
    if (
      previous &&
      previous.signature === signature &&
      previous.states.length === states.length &&
      states.every((state, index) => state === previous.states[index])
    ) {
      return previous.value;
    }
    const value: Record<string, ResourceState<T>> = {};
    for (const [index, key] of list.entries()) {
      const state = states[index];
      if (state) value[key] = state;
    }
    cache.current = { signature, states, value };
    return value;
  }, [signature, store]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function splitKeys(signature: string): string[] {
  return signature === "" ? [] : signature.split(" ");
}

/** What every one of these feeds is playing, from one poller each. */
export function useNowPlayingAll(
  feedIds: readonly string[],
): Record<string, FeedNowPlaying> {
  return useStoreMap(nowPlayingStore, feedIds);
}

/** What the product has established about every one of these feeds. */
export function useFeedHealthAll(
  feedIds: readonly string[],
): Record<string, FeedHealthState> {
  return useStoreMap(feedHealthStore, feedIds);
}

export interface NextUpRequest {
  feedId: string;
  /** What is airing now. The plan advances exactly when this changes. */
  revision: string;
}

/**
 * What is next on each of several feeds.
 *
 * Only the feeds whose airing track actually changed are asked again — the plan for a
 * feed that has not moved on has not moved either, and re-reading every feed because
 * one of them changed track would make a console of ten feeds ten times as expensive
 * as it needs to be.
 */
export function useNextUpAll(
  requests: readonly NextUpRequest[],
): Record<string, Decision[]> {
  const [plans, setPlans] = useState<Record<string, Decision[]>>({});
  const read = useRef(new Map<string, string>());
  const signature = requests
    .map((request) => `${request.feedId}@${request.revision}`)
    .join(" ");

  useEffect(() => {
    const controller = new AbortController();
    const wanted = new Map(
      splitKeys(signature).map((entry) => {
        const at = entry.lastIndexOf("@");
        return [entry.slice(0, at), entry.slice(at + 1)] as const;
      }),
    );
    // A feed that is no longer on screen leaves nothing behind to go stale.
    for (const feedId of read.current.keys()) {
      if (!wanted.has(feedId)) read.current.delete(feedId);
    }
    for (const [feedId, revision] of wanted) {
      if (read.current.get(feedId) === revision) continue;
      read.current.set(feedId, revision);
      fetchNextUp(feedId, controller.signal)
        .then((decisions) => {
          if (controller.signal.aborted) return;
          setPlans((current) => ({ ...current, [feedId]: decisions }));
        })
        .catch(() => {
          // A plan that cannot be read is shown as no plan, beside a now-playing
          // that is still live. It is not worth a second error surface.
          if (!controller.signal.aborted) read.current.delete(feedId);
        });
    }
    return () => {
      controller.abort();
    };
  }, [signature]);

  return plans;
}

export interface AsyncValue<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

export interface Reloadable<T> extends AsyncValue<T> {
  reload: () => void;
}

/**
 * A read that is not on a clock: it happens when the screen opens and again when
 * something the operator did makes the old answer wrong. The loader is held in a ref
 * so that passing a fresh closure on every render does not restart the request —
 * what decides when to read again is `revision`, and nothing else.
 */
function useRead<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  revision: string,
  enabled = true,
): Reloadable<T> {
  const load = useRef(loader);
  load.current = loader;
  const [nonce, setNonce] = useState(0);
  const [value, setValue] = useState<AsyncValue<T>>({
    data: null,
    error: null,
    loading: enabled,
  });

  useEffect(() => {
    if (!enabled) {
      setValue({ data: null, error: null, loading: false });
      return;
    }
    const controller = new AbortController();
    setValue((current) => ({ ...current, loading: current.data === null }));
    load
      .current(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setValue({ data, error: null, loading: false });
        }
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setValue({
          data: null,
          error: error instanceof Error ? error.message : String(error),
          loading: false,
        });
      });
    return () => {
      controller.abort();
    };
  }, [enabled, revision, nonce]);

  const reload = useCallback(() => {
    setNonce((current) => current + 1);
  }, []);

  return { ...value, reload };
}

/**
 * A feed's recent history. It is re-read when that feed changes track — `revision` is
 * the airing that is current — rather than on a clock of its own, because the history
 * changes exactly when the now-playing does and at no other time.
 */
export function useFeedHistory(
  feedId: string,
  revision: string | null,
  enabled: boolean,
): AsyncValue<Airing[]> {
  return useRead<Airing[]>(
    (signal) => fetchHistory(feedId, 12, signal),
    `${feedId}:${revision ?? ""}`,
    enabled && feedId !== "",
  );
}

/**
 * What the brain has decided will play next on one feed. Read on the same trigger as
 * the history: the plan advances when a track starts, so that is when it is worth
 * asking again.
 */
export function useFeedNextUp(
  feedId: string,
  revision: string | null,
  enabled: boolean,
): AsyncValue<Decision[]> {
  return useRead<Decision[]>(
    (signal) => fetchNextUp(feedId, signal),
    `${feedId}:${revision ?? ""}`,
    enabled && feedId !== "",
  );
}

/** Every feed the operator has, disabled ones included. */
export function useFeeds(enabled: boolean): Reloadable<Feed[]> {
  return useRead<Feed[]>((signal) => fetchFeeds(signal), "feeds", enabled);
}

/** One feed's row — its identity, its programming references and its audio. */
export function useFeed(
  feedId: string,
  enabled: boolean,
): Reloadable<Feed | null> {
  return useRead<Feed | null>(
    (signal) => fetchFeed(feedId, signal),
    `feed:${feedId}`,
    enabled && feedId !== "",
  );
}

/** Everything one feed publishes, including the deliveries it has switched off. */
export function useFeedOutputs(
  feedId: string,
  enabled: boolean,
): Reloadable<Output[]> {
  return useRead<Output[]>(
    (signal) => fetchOutputs(feedId, signal),
    `outputs:${feedId}`,
    enabled && feedId !== "",
  );
}

/** The tolerances one feed is judged against, as its own editable resource. */
export function useHealthPolicy(
  feedId: string,
  enabled: boolean,
): Reloadable<HealthPolicy> {
  return useRead<HealthPolicy>(
    (signal) => fetchHealthPolicy(feedId, signal),
    `policy:${feedId}`,
    enabled && feedId !== "",
  );
}

export function usePlaylists(enabled: boolean): Reloadable<Playlist[]> {
  return useRead<Playlist[]>(
    (signal) => fetchPlaylists(signal),
    "playlists",
    enabled,
  );
}

export function useSchedules(enabled: boolean): Reloadable<Schedule[]> {
  return useRead<Schedule[]>(
    (signal) => fetchSchedules(signal),
    "schedules",
    enabled,
  );
}

/** One playlist with its ordered items. Re-read after every write that changes it. */
export function usePlaylist(
  playlistId: string,
  enabled: boolean,
): Reloadable<PlaylistDetail | null> {
  return useRead<PlaylistDetail | null>(
    (signal) => fetchPlaylist(playlistId, signal),
    `playlist:${playlistId}`,
    enabled && playlistId !== "",
  );
}

/** One schedule's slots. The grid is drawn from exactly this and nothing else. */
export function useSlots(
  scheduleId: string,
  enabled: boolean,
): Reloadable<ScheduleSlot[]> {
  return useRead<ScheduleSlot[]>(
    (signal) => fetchSlots(scheduleId, signal),
    `slots:${scheduleId}`,
    enabled && scheduleId !== "",
  );
}

/**
 * Every named schedule's slots at once, which is what "who uses this playlist" needs:
 * a feed reaches a playlist through the schedule it is assigned, so the answer is
 * incomplete until those slots have been read. Schedules whose slots have not arrived
 * yet are simply absent, so the answer grows rather than being wrong in the meantime.
 */
export function useSlotsForSchedules(
  scheduleIds: readonly string[],
): Record<string, ScheduleSlot[]> {
  const [slots, setSlots] = useState<Record<string, ScheduleSlot[]>>({});
  // Schedule ids are uuids, so a space is an unambiguous join and the signature is
  // what decides when this reads again — not a fresh array on every render.
  const signature = [...new Set(scheduleIds)].sort().join(" ");

  useEffect(() => {
    const controller = new AbortController();
    const wanted = signature === "" ? [] : signature.split(" ");
    if (wanted.length === 0) {
      setSlots({});
      return;
    }
    Promise.all(
      wanted.map((scheduleId) =>
        fetchSlots(scheduleId, controller.signal)
          .then((rows) => [scheduleId, rows] as const)
          .catch(() => null),
      ),
    )
      .then((entries) => {
        if (controller.signal.aborted) return;
        const next: Record<string, ScheduleSlot[]> = {};
        for (const entry of entries) if (entry) next[entry[0]] = entry[1];
        setSlots(next);
      })
      .catch(() => {
        // A schedule whose slots cannot be read contributes nothing to the answer;
        // it is not worth a second error surface on a screen about playlists.
      });
    return () => {
      controller.abort();
    };
  }, [signature]);

  return slots;
}

export interface PagedList<T> {
  items: T[];
  /** True only while the first page of the current request is in flight. */
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

/**
 * A collection the API pages, read one page at a time and appended to.
 *
 * `revision` is what the first page depends on — a library search, say. When it
 * changes the collection is read again from the beginning, because the pages already
 * loaded were answers to a different question; asking for more appends to it. The
 * loader is held in a ref so a fresh closure per render never restarts a read.
 */
function usePagedCollection<T>(
  load: (
    cursor: string | null,
    signal: AbortSignal | undefined,
  ) => Promise<Page<T>>,
  revision: string,
): PagedList<T> {
  const read = useRef(load);
  read.current = load;
  const [state, setState] = useState<{
    items: T[];
    cursor: string | null;
    hasMore: boolean;
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
  }>({
    items: [],
    cursor: null,
    hasMore: false,
    loading: true,
    loadingMore: false,
    error: null,
  });
  const [nonce, setNonce] = useState(0);
  const request = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const ticket = request.current + 1;
    request.current = ticket;
    setState({
      items: [],
      cursor: null,
      hasMore: false,
      loading: true,
      loadingMore: false,
      error: null,
    });
    read
      .current(null, controller.signal)
      .then((page) => {
        if (controller.signal.aborted || request.current !== ticket) return;
        setState({
          items: page.items,
          cursor: page.nextCursor,
          hasMore: page.nextCursor !== null,
          loading: false,
          loadingMore: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || request.current !== ticket) return;
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setState({
          items: [],
          cursor: null,
          hasMore: false,
          loading: false,
          loadingMore: false,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    return () => {
      controller.abort();
    };
  }, [nonce, revision]);

  const loadMore = useCallback(() => {
    setState((current) => {
      if (current.loading || current.loadingMore || current.cursor === null) {
        return current;
      }
      const ticket = request.current;
      void read
        .current(current.cursor, undefined)
        .then((page) => {
          if (request.current !== ticket) return;
          setState((latest) => ({
            ...latest,
            items: [...latest.items, ...page.items],
            cursor: page.nextCursor,
            hasMore: page.nextCursor !== null,
            loadingMore: false,
          }));
        })
        .catch((error: unknown) => {
          if (request.current !== ticket) return;
          setState((latest) => ({
            ...latest,
            loadingMore: false,
            error: error instanceof Error ? error.message : String(error),
          }));
        });
      return { ...current, loadingMore: true };
    });
  }, []);

  const reload = useCallback(() => {
    setNonce((current) => current + 1);
  }, []);

  return {
    items: state.items,
    loading: state.loading,
    loadingMore: state.loadingMore,
    error: state.error,
    hasMore: state.hasMore,
    loadMore,
    reload,
  };
}

/**
 * The library. The API filters before it cuts the page, so a search is a new request
 * rather than a filter over what happens to be on screen — which is the only way a
 * search over a library larger than a page can tell the truth.
 */
export function useTrackLibrary(
  search: string,
  pageSize = 50,
): PagedList<Track> {
  return usePagedCollection<Track>(
    (cursor, signal) =>
      fetchTrackPage({ search, cursor, limit: pageSize, signal }),
    `${String(pageSize)}:${search}`,
  );
}

/** The playlist collection, which the API pages exactly as it pages the library. */
export function usePlaylistPages(pageSize = 50): PagedList<Playlist> {
  return usePagedCollection<Playlist>(
    (cursor, signal) => fetchPlaylistPage(cursor, pageSize, signal),
    `playlists:${String(pageSize)}`,
  );
}

export function useFeedDirectory(): AsyncValue<FeedDirectory> {
  return useRead<FeedDirectory>(
    (signal) => loadFeedDirectory(signal),
    "directory",
  );
}

/** Who is signed in, or nobody. Every screen that needs a session reads this. */
export function useSession(): OperatorSession | null {
  return useSyncExternalStore(subscribeSession, readSession, () => null);
}
